/**
 * A project: the icon and name (click to rename), then chats, instructions,
 * design mode and the design system its chats follow, memory and files, from the prototype (lines ~251-296). Share is hidden
 * until sharing exists; the delete confirm at the foot is this builder's own
 * addition, since a destructive action needs one and the prototype's mock
 * never had to ask.
 */

import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  deleteFile,
  deleteProject,
  deleteProjectMemory,
  fileUrl,
  getProject,
  patchProject,
  uploadFile,
  type FileInfo,
  type Project
} from '../api';
import { fileIcon } from '../components/Attachments';
import { relativeTime } from '../components/settings/format';
import { useShell } from '../components/Shell';
import { MonoLabel, PROJECT_COLOURS, Ph } from '../components/ui';
import { ProjectDesign } from '../components/DesignControls';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { useApp } from '../state';

function ProjectTitle({ project, onRenamed }: { project: Project; onRenamed: (name: string) => void }) {
  const { refreshLists } = useApp();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(project.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setValue(project.name), [project.name]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const next = value.trim();
    if (!next || next === project.name) {
      setValue(project.name);
      return;
    }
    onRenamed(next);
    patchProject(project.id, { name: next })
      .then(() => void refreshLists())
      .catch(() => onRenamed(project.name));
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            setValue(project.name);
            setEditing(false);
          }
        }}
        style={{
          flex: 1,
          minWidth: 200,
          border: 0,
          borderBottom: '2px solid var(--primary)',
          background: 'transparent',
          padding: 0,
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: 36,
          lineHeight: 1.02,
          color: 'var(--text-strong)'
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="bare hov-strong"
      onClick={() => setEditing(true)}
      style={{
        flex: 1,
        minWidth: 200,
        textAlign: 'left',
        fontFamily: 'var(--font-display)',
        fontWeight: 800,
        fontSize: 36,
        lineHeight: 1.02,
        color: 'var(--text-strong)'
      }}
    >
      {project.name}.
    </button>
  );
}

function InstructionsField({ project, onSaved }: { project: Project; onSaved: (instructions: string) => void }) {
  const [value, setValue] = useState(project.instructions);
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => setValue(project.instructions), [project.id]);
  useEffect(
    () => () => {
      clearTimeout(saveTimer.current);
      clearTimeout(savedTimer.current);
    },
    []
  );

  const onChange = (next: string) => {
    setValue(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      patchProject(project.id, { instructions: next })
        .then(() => {
          onSaved(next);
          setSaved(true);
          clearTimeout(savedTimer.current);
          savedTimer.current = setTimeout(() => setSaved(false), 1600);
        })
        .catch(() => {
          // Left as a draft in the field; the next keystroke tries again.
        });
    }, 800);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MonoLabel style={{ flex: 1 }}>Instructions</MonoLabel>
        {saved ? <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>Saved</span> : null}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        style={{
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-card)',
          borderRadius: 'var(--radius-app-card)',
          padding: '12px 14px',
          fontSize: 13.5,
          lineHeight: 1.55,
          color: 'var(--text-body)',
          resize: 'vertical'
        }}
      />
    </div>
  );
}

function FileTile({ file, onRemoved }: { file: FileInfo; onRemoved: () => void }) {
  const [hover, setHover] = useState(false);
  const [busy, setBusy] = useState(false);
  const isImage = file.mime.startsWith('image/');

  return (
    <div
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <a href={fileUrl(file.id)} target="_blank" rel="noopener noreferrer" title={file.name}>
        <div
          style={{
            height: 72,
            borderRadius: 'var(--radius-app-card)',
            background: 'var(--surface-card)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-faint)',
            overflow: 'hidden'
          }}
        >
          {isImage ? (
            <img src={fileUrl(file.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Ph name={fileIcon(file.mime)} size={22} />
          )}
        </div>
      </a>
      <span
        style={{
          fontSize: 12,
          color: 'var(--text-body)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {file.name}
      </span>
      {hover ? (
        <button
          type="button"
          className="bare hov-strong"
          aria-label={`Remove ${file.name}`}
          disabled={busy}
          onClick={(e) => {
            e.preventDefault();
            setBusy(true);
            deleteFile(file.id)
              .then(onRemoved)
              .catch(() => setBusy(false));
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 20,
            height: 20,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 999,
            background: 'var(--surface-card)',
            color: 'var(--text-faint)',
            boxShadow: 'var(--shadow-xs)'
          }}
        >
          <Ph name="x" size={11} />
        </button>
      ) : null}
    </div>
  );
}

export function ProjectPage({ id }: { id: string }) {
  const { refreshLists } = useApp();
  const { narrow } = useShell();
  const { navigate } = useRouter();
  const { Button, Dialog } = ds();

  const [project, setProject] = useState<Project | null>(null);
  const [failed, setFailed] = useState<'missing' | 'failed' | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setProject(null);
    setFailed(null);
    let cancelled = false;
    getProject(id)
      .then((p) => {
        if (!cancelled) setProject(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFailed(err instanceof ApiError && err.status === 404 ? 'missing' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [id, attempt]);

  if (failed === 'missing') {
    return (
      <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '40px 24px' }}>
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
          There is no project here.{' '}
          <Link to="/" style={{ color: 'var(--text-link)' }}>
            Go home
          </Link>
        </span>
      </div>
    );
  }

  if (failed === 'failed') {
    return (
      <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: '40px 24px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-muted)' }}>
          <Ph name="warning-circle" size={15} color="var(--danger-500)" />
          This project did not load.
          <button type="button" className="bare" onClick={() => setAttempt((n) => n + 1)} style={{ color: 'var(--text-link)', fontSize: 13.5 }}>
            Retry
          </button>
        </span>
      </div>
    );
  }

  if (!project) return <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }} />;

  const colours = PROJECT_COLOURS[project.colour];

  const addFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    uploadFile(file, { projectId: id })
      .then((info) => setProject((p) => (p ? { ...p, files: [...p.files, info] } : p)))
      .catch(() => {
        // Nothing was added; the + is still there to try again.
      })
      .finally(() => setUploading(false));
  };

  return (
    <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: narrow ? '16px 16px 40px' : '16px 28px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span
            style={{
              width: 44,
              height: 44,
              flex: 'none',
              borderRadius: 12,
              background: colours?.soft ?? 'var(--primary-soft)',
              color: colours?.dot ?? 'var(--primary)',
              display: 'grid',
              placeItems: 'center'
            }}
          >
            <Ph name={project.icon} size={22} />
          </span>
          <ProjectTitle project={project} onRenamed={(name) => setProject((p) => (p ? { ...p, name } : p))} />
          <Button
            variant="accent"
            density="app"
            size="sm"
            iconLeft="note-pencil"
            onClick={() => navigate('/?project=' + id)}
          >
            New chat
          </Button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MonoLabel>Chats</MonoLabel>
            <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {project.chats.map((c) => (
                <Link
                  key={c.id}
                  to={`/chat/${c.id}`}
                  className="hov-tint"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    minHeight: 56,
                    padding: '0 16px',
                    borderBottom: '1px solid var(--border-subtle)'
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.title || 'New chat'}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{relativeTime(c.updatedAt)}</span>
                </Link>
              ))}
              {!project.chats.length ? (
                <div style={{ padding: '18px 16px', fontSize: 13.5, color: 'var(--text-muted)' }}>No chats yet.</div>
              ) : null}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <InstructionsField
              project={project}
              onSaved={(instructions) => setProject((p) => (p ? { ...p, instructions } : p))}
            />

            <ProjectDesign
              design={project.design}
              designSystemId={project.designSystemId}
              onChange={(patch) => {
                const before = { design: project.design, designSystemId: project.designSystemId };
                setProject((p) => (p ? { ...p, ...patch } : p));
                patchProject(project.id, patch).catch(() => setProject((p) => (p ? { ...p, ...before } : p)));
              }}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <MonoLabel>Memory</MonoLabel>
              <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {project.memory.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      minHeight: 42,
                      padding: '0 8px 0 14px',
                      borderBottom: '1px solid var(--border-subtle)',
                      fontSize: 13.5,
                      color: 'var(--text-body)'
                    }}
                  >
                    <span style={{ flex: 1 }}>{m.text}</span>
                    <button
                      type="button"
                      className="bare hov-soft"
                      aria-label="Forget"
                      title="Forget"
                      onClick={() => {
                        setProject((p) => (p ? { ...p, memory: p.memory.filter((x) => x.id !== m.id) } : p));
                        deleteProjectMemory(id, m.id).catch(() => {
                          // The next full load will put it back; not worth a retry prompt.
                        });
                      }}
                      style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 6, color: 'var(--text-faint)' }}
                    >
                      <Ph name="x" size={12} />
                    </button>
                  </div>
                ))}
                <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-faint)' }}>
                  Assist adds to this as you chat.
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MonoLabel style={{ flex: 1 }}>Files</MonoLabel>
                <button
                  type="button"
                  className="bare hov-strong"
                  disabled={uploading}
                  onClick={() => fileInput.current?.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: 'var(--text-link)' }}
                >
                  <Ph name={uploading ? 'circle-notch' : 'plus'} size={12} className={uploading ? 'spin' : undefined} />
                  Add
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  hidden
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
                {project.files.map((f) => (
                  <FileTile
                    key={f.id}
                    file={f}
                    onRemoved={() => setProject((p) => (p ? { ...p, files: p.files.filter((x) => x.id !== f.id) } : p))}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <Button variant="ghost" density="app" size="sm" onClick={() => setConfirmDelete(true)}>
            Delete project
          </Button>
        </div>
      </div>

      {confirmDelete ? (
        <Dialog
          open
          position="fixed"
          width={420}
          title={`Delete "${project.name}"?`}
          onClose={() => setConfirmDelete(false)}
          style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16 }}
          footer={
            <>
              <Button variant="ghost" density="app" size="sm" disabled={deleting} onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                density="app"
                size="sm"
                disabled={deleting}
                onClick={() => {
                  setDeleting(true);
                  deleteProject(id)
                    .then(() => void refreshLists())
                    .then(() => navigate('/'))
                    .catch(() => setDeleting(false));
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-body)' }}>
            Its chats stay, without a project. Its memory and files go with it.
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}
